<template>
  <rest-form-page
    :page-title="pageTitle"
    :fetch-endpoint="fetchEndpoint"
    :save-endpoint="saveEndpoint"
    :after-save="afterSave"
    :model="model"
    :readonly="readonly"
    :rules="rules"
    :is-disabled-save-button="isDisabledSaveButton"
  >
    <template slot="form" v-if="model">
      <form-section>
        <el-row>
          <el-col>
            <rest-form-item prop="name" :label="$t('SnippetForm_Name')">
              <el-input v-model="model.name" />
            </rest-form-item>
          </el-col>
        </el-row>

        <el-row>
          <el-col>
            <rest-form-item prop="content" :label="$t('SnippetForm_Content')">
              <snippet-text-editor
                v-if="getType === 1"
                :model-field="model.content"
                @updateContent="updateContent"
                :readonly="readonly"
              />
              <ck-editor
                v-if="getType === 2"
                :content="model.content"
                :on-change="updateContent"
                ref="editor"
                :readonly="readonly"
                :full-page="false"
                :allowed-content="true"
                @source-btn="toggleSaveButton"
              />
            </rest-form-item>
          </el-col>
        </el-row>
      </form-section>
    </template>
  </rest-form-page>
</template>

<script>
import RestFormPage from '@/common/components/rest/RestFormPage'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import FormSection from '@/common/components/form/FormSection'
import RestFormItem from '@/common/components/rest/RestFormItem'
import SnippetTextEditor from '@/modules/snippets/components/SnippetTextInput'
import CkEditor from 'vendor/ckeditor/editor'

export default {
  name: 'SnippetFormPage',

  components: {
    SnippetTextEditor,
    CkEditor,
    RestFormItem,
    FormSection,
    RestFormPage
  },

  props: {
    readonly: {
      default: false,
      type: Boolean
    },
    copy: {
      default: false,
      type: Boolean
    }
  },

  data() {
    let checkSnippetName = (rule, value, callback) => {
      if (value.split(' ').length > 1) {
        return callback(new Error('Name can not contain spaces'))
      } else {
        callback()
      }
    }

    return {
      model: this.defaultModel(),
      rules: {
        name: [{ validator: checkSnippetName }]
      },
      isDisabledSaveButton: false
    }
  },

  computed: {
    pageTitle() {
      return this.$t(this.$route.meta.title)
    },

    fetchEndpoint() {
      if (this.$route.query.id) {
        return projectEndpoint({
          method: 'get',
          url: `snippets/${this.$route.query.id}`,
          handler: data => {
            this.model = data
          }
        })
      } else {
        return null
      }
    },

    saveEndpoint() {
      if (this.$route.query.id && !this.copy) {
        return projectEndpoint({
          method: 'patch',
          url: `snippets/${this.$route.query.id}`
        })
      } else {
        return projectEndpoint({
          method: 'post',
          url: 'snippets'
        })
      }
    },

    getType() {
      return Number(this.model.type ? this.model.type : this.$route.query.type)
    }
  },

  methods: {
    defaultModel() {
      if (this.$route.query.id) {
        return null
      } else {
        return {
          id: '',
          name: '',
          type: this.$route.query.type,
          content: ''
        }
      }
    },

    afterSave() {
      this.$router.push(
        projectRoute({
          name: 'Snippets'
        })
      )
    },

    updateContent(content) {
      if (this.getType === 1) {
        this.model.content = content
      }

      if (this.getType === 2) {
        this.model.content = this.$refs.editor.updatedContent()
      }
    },

    toggleSaveButton() {
      this.isDisabledSaveButton = !this.isDisabledSaveButton
    }
  }
}
</script>

<style scoped>
>>> #createSnippetContentBlock input[type='text'],
>>> #createSnippetContentBlock select,
>>> #createSnippetContentBlock .select_label {
  width: 75%;
}

>>> .snippet-text-editor textarea {
  margin: 0 !important;
  border: 1px solid #c2c4c6;
}

>>> .contentEditor {
  position: relative;
}

>>> .emojisEditor .emojisPopup {
  display: none;
  position: absolute;
  z-index: 2002;
  width: 310px;
  right: -1px;
  top: 35px;
  overflow: hidden;
  box-shadow: 0 1px 7px rgba(0, 0, 0, 0.25);
  background: #fff;
}

>>> .emojisEditor .emojisPopup .emojisMenu {
  background: #eaeaea;
}

>>> .emojisEditor .emojisPopup .emojisItem {
  display: inline-block;
  font-size: 16px;
  text-align: center;
  width: 30px;
  margin: 4px;
  cursor: pointer;
}

>>> .emojisEditor .emojisPopup .emojisCategory {
  height: 160px;
  overflow-x: hidden;
  overflow-y: auto;
}

>>> .emojisEditor .emojisPopup .emojisTitle {
  margin: 4px;
  font-weight: 700;
  color: #666;
}

>>> .emojisEditor .emojisPopup .emojisItem {
  display: inline-block;
  font-size: 16px;
  text-align: center;
  width: 30px;
  margin: 4px;
  cursor: pointer;
}

>>> .personalizationEditor .personalizationPopup {
  display: none;
  position: absolute;
  z-index: 2002;
  width: 400px;
  right: -1px;
  top: 35px;
  overflow: hidden;
  box-shadow: 0 1px 7px rgba(0, 0, 0, 0.25);
  padding: 12px;
  background: #fff;
}

>>> .personalizationEditor .personalizationPopupRemoveButton {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 16px;
  cursor: pointer;
}

>>> .contentEditor.snippetEditor textarea {
  height: 200px;
  width: 100%;
  box-sizing: border-box;
}

>>> input[type='email'],
>>> input[type='number'],
>>> input[type='password'],
>>> input[type='search'],
>>> input[type='text'],
>>> input[type='url'] {
  background: #fff;
  border: 1px solid #c2c4c6;
  cursor: text;
  height: 38px;
  line-height: 38px;
  outline: none;
  padding: 0 10px !important;
  color: #717782 !important;
}

>>> .label1 {
  color: #6e6e6e;
  font: 13px Open Sans, sans-serif;
  text-transform: uppercase;
}

>>> .snippetEditor .snippetPopup {
  display: none;
  position: absolute;
  z-index: 2002;
  width: 400px;
  right: -1px;
  top: 35px;
  overflow: hidden;
  box-shadow: 0 1px 7px rgba(0, 0, 0, 0.25);
  padding: 12px;
  background: #fff;
}

>>> .contentEditor .contentEditorToolbar {
  position: absolute;
  z-index: 1100;
  top: 1px;
  right: 25px;
  height: 50px;
  width: 70px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 0 !important;
}
>>> .contentEditor .contentEditorToolbar i {
  color: #111;
  cursor: pointer;
  font-size: 12px;
}

>>> .contentEditor .contentEditorToolbar span {
  color: #111;
  cursor: pointer;
  font-size: 15px;
}

>>> .icon {
  >>>.contenteditor.contenteddisplay: inline-block;
  overflow: hidden;
}

>>> .snippetEditor .snippetPopupRemoveButton {
  position: absolute;
  top: 6px;
  right: 6px;
  font-size: 16px;
  cursor: pointer;
}

>>> input:hover[type='password'],
>>> input:hover[type='text'],
>>> select:hover,
>>> textarea:hover {
  border-color: #1d87c8 !important;
}

>>> .stdTextarea {
  display: block;
  margin: 0;
  background: #fff;
  border: 1px solid #ccc;
  outline: none;
  color: #717782;
  font-size: 16px;
  font-weight: 300;
  line-height: 22px;
}
>>> textarea {
  width: 100%;
  resize: vertical;
  min-height: 200px;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  padding: 10px;
  margin: 10px 0;
  font: 12pt/12pt Open Sans, sans-serif;
}
</style>



// WEBPACK FOOTER //
// src/modules/snippets/pages/SnippetFormPage.vue